/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';

import '../styles/camera.css';

const HTMLCameraContext = React.createContext(undefined);

export type TakePhoto = () => void;

export function useHTMLCamera() {
  const context = React.useContext(HTMLCameraContext);
  if (!context) throw new Error('useHTMLCamera must be used inside HTMLCamera');
  return context;
}

export default function HTMLCamera({ children }: React.PropsWithChildren) {
  const [error, setError] = React.useState(undefined);
  const [open, setOpen] = React.useState(false);
  const [callback, setCallback] = React.useState<TakePhoto | undefined>();

  const video = React.useRef<HTMLVideoElement>(null);

  const takePhoto = React.useCallback(async (cb: TakePhoto) => {
    setOpen(true);
    setCallback(cb);

    try {
      const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      video.current.srcObject = media;
      video.current.play();
    } catch (err: any) {
      setError(err);
    }

    cb();
  }, []);

  const handleClose = () => {
    if (callback) callback();
    setCallback(undefined);
    setOpen(false);
  };

  const values = React.useMemo(() => ({
    takePhoto,
  }), [takePhoto]);

  return (
    <HTMLCameraContext.Provider value={values}>
      {children}
      {
        open && (
          <div className="camera">
            {
              !error && (
                <div className="camera-contents">
                  <video ref={video} />
                  <div>Take Photo, Cancel</div>
                </div>
              )
            }
            {
              error && (
                <div className="camera-error">
                  <div className="camera-error-message">
                    There was an error while attempting to start your
                    camera. Please contact support or try again.
                  </div>
                  <div>
                    <button type="button" className="btn" onClick={handleClose}>Close</button>
                  </div>
                </div>
              )
            }
          </div>
        )
      }
    </HTMLCameraContext.Provider>
  );
}
